import { useMemo, useState, ReactNode } from 'react';
import {
  MessageSquare,
  ShieldAlert,
  Search,
  X,
  Inbox,
  Trash2,
  MessagesSquare,
  Flag,
  Ban,
} from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import {
  useMessageListQuery,
  useMessageOverviewQuery,
} from '@/client/api/messaging/messaging.query';
import {
  AdminMessage,
  formatRelativeEpoch,
  isInquiryMessage,
  MessageListParams,
  MessageThreadParams,
  messageEntity,
  STATUS_BADGE,
} from '@/client/types/messaging';
import { useListFilters } from '@/client/hooks/useListFilters';
import { useBreadcrumbs } from '@/client/hooks/use-breadcrumbs';
import { useHasPermission } from '@/client/hooks/useHasPermission';
import Pagination from '@/client/components/common/Pagination';
import MessageDetailsDrawer from '@/client/components/admin/Messaging/MessageDetailsDrawer';
import PartyThreadDrawer from '@/client/components/admin/Messaging/PartyThreadDrawer';
import { MessageParties } from '@/client/components/admin/Messaging/PartyHoverCard';

interface MessageFilters {
  is_deleted: string;
  is_platform: string;
  status: string;
  sender_kind: string;
  attachment_kind: string;
  sort: string;
  order: string;
}

const MESSAGE_SORT_OPTIONS = [
  { value: 'timestamp', label: 'Timestamp' },
  { value: 'created_at', label: 'Created' },
  { value: 'updated_at', label: 'Updated' },
  { value: 'priority', label: 'Priority' },
  { value: 'seq', label: 'Seq' },
  { value: 'is_read', label: 'Read' },
  { value: 'is_delivered', label: 'Delivered' },
  { value: 'is_deleted', label: 'Deleted' },
];

const selectCls =
  'min-w-[130px] rounded-md border border-gray-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500';

const StatCard = ({
  label,
  value,
  icon,
  color,
}: {
  label: string;
  value: string | number;
  icon: ReactNode;
  color: string;
}) => (
  <div className="rounded-xl border border-gray-100 bg-white p-5 shadow-sm">
    <div className="flex items-center gap-4">
      <div className={`flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-xl ${color}`}>
        {icon}
      </div>
      <div>
        <p className="text-sm font-medium text-gray-500">{label}</p>
        <p className="mt-0.5 text-2xl font-black text-gray-900">{value}</p>
      </div>
    </div>
  </div>
);

const AccessDenied = () => (
  <div className="flex flex-col items-center justify-center gap-3 rounded-xl border border-gray-100 bg-white py-20 text-center shadow-sm">
    <div className="flex h-14 w-14 items-center justify-center rounded-full bg-red-50">
      <ShieldAlert className="h-7 w-7 text-red-500" />
    </div>
    <h2 className="text-lg font-semibold text-gray-800">Access restricted</h2>
    <p className="max-w-sm text-sm text-gray-500">
      Viewing messages requires the{' '}
      <code className="rounded bg-gray-100 px-1.5 py-0.5 text-xs">message.view</code> permission.
    </p>
  </div>
);

const MessagingInboxPage = () => {
  useBreadcrumbs([
    { label: 'Communications', href: '/admin/communications/messaging' },
    { label: 'Messaging' },
    { label: 'Messages' },
  ]);

  const viewPerm = useHasPermission('message.view');
  const moderatePerm = useHasPermission('message.moderate');
  const deletePerm = useHasPermission('message.delete');

  const canView = viewPerm.isSuperAdmin || viewPerm.hasPermission;
  const canModerate = moderatePerm.isSuperAdmin || moderatePerm.hasPermission;
  const canDelete = deletePerm.isSuperAdmin || deletePerm.hasPermission;
  const permLoading = viewPerm.isLoading;

  const [selectedRef, setSelectedRef] = useState<string | null>(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [threadParams, setThreadParams] = useState<MessageThreadParams | null>(null);
  const [isThreadOpen, setIsThreadOpen] = useState(false);

  const {
    searchTerm,
    debouncedSearchTerm,
    filters,
    page,
    limit,
    handleSearch,
    handleFilterChange,
    handlePageChange,
    handleLimitChange,
    resetFilters,
  } = useListFilters<MessageFilters>({
    initialFilters: {
      is_deleted: 'false',
      is_platform: '',
      status: '',
      sender_kind: '',
      attachment_kind: '',
      sort: 'timestamp',
      order: 'desc',
    },
    initialLimit: 25,
  });

  const listParams: MessageListParams = useMemo(() => {
    const q = debouncedSearchTerm.trim();
    const asUid = q && /^\d+$/.test(q) ? Number(q) : undefined;
    return {
      limit,
      offset: (page - 1) * limit,
      sort: filters.sort || 'timestamp',
      order: filters.order || 'desc',
      ...(asUid != null ? { uid: asUid } : q ? { q } : {}),
      ...(filters.is_deleted === 'true' || filters.is_deleted === 'false'
        ? { is_deleted: filters.is_deleted }
        : {}),
      ...(filters.is_platform === 'true' || filters.is_platform === 'false'
        ? { is_platform: filters.is_platform }
        : {}),
      ...(filters.status ? { status: filters.status } : {}),
      ...(filters.sender_kind ? { sender_kind: filters.sender_kind } : {}),
      ...(filters.attachment_kind ? { attachment_kind: filters.attachment_kind } : {}),
    };
  }, [debouncedSearchTerm, filters, page, limit]);

  const { data, isLoading, isError, error } = useMessageListQuery(
    listParams,
    canView && !permLoading,
  );
  const { data: overview } = useMessageOverviewQuery(canView && !permLoading);

  const messages = data?.messages ?? [];
  const pagination = data?.pagination;
  const forbidden =
    (error as { status?: number })?.status === 403 || (!permLoading && !canView);
  const hasFilters =
    !!searchTerm ||
    filters.is_deleted !== 'false' ||
    filters.is_platform !== '' ||
    !!filters.status ||
    !!filters.sender_kind ||
    !!filters.attachment_kind ||
    filters.sort !== 'timestamp' ||
    filters.order !== 'desc';

  if (permLoading) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="mx-auto max-w-screen-2xl space-y-4">
          <Skeleton className="h-10 w-48" />
          <Skeleton className="h-24 w-full" />
        </div>
      </div>
    );
  }

  if (forbidden) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="mx-auto max-w-screen-2xl">
          <AccessDenied />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="mx-auto max-w-screen-2xl space-y-6">
        <div>
          <div className="mb-1 flex items-center gap-2.5">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-blue-600">
              <MessageSquare className="h-4 w-4 text-white" />
            </div>
            <h1 className="text-2xl font-black text-gray-900">Messages</h1>
          </div>
          <p className="text-sm text-gray-500">
            Search and moderate user, org, and platform messages in the canonical store.
          </p>
        </div>

        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <StatCard
            label="Total messages"
            value={overview?.messages_total ?? '—'}
            icon={<Inbox className="h-5 w-5 text-blue-700" />}
            color="bg-blue-50"
          />
          <StatCard
            label="Deleted"
            value={overview?.messages_deleted ?? '—'}
            icon={<Trash2 className="h-5 w-5 text-red-600" />}
            color="bg-red-50"
          />
          <StatCard
            label="Conversations"
            value={overview?.conversations_total ?? '—'}
            icon={<MessagesSquare className="h-5 w-5 text-indigo-600" />}
            color="bg-indigo-50"
          />
          <StatCard
            label="Pending reports"
            value={overview?.reports_pending ?? '—'}
            icon={<Flag className="h-5 w-5 text-amber-700" />}
            color="bg-amber-50"
          />
        </div>

        <div className="rounded-lg bg-white p-4 shadow">
          <div className="flex flex-col gap-3 lg:flex-row lg:flex-wrap">
            <div className="relative min-w-[200px] flex-1">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search body or filter by UID…"
                value={searchTerm}
                onChange={(e) => handleSearch(e.target.value)}
                className="w-full rounded-md border border-gray-300 py-2 pl-9 pr-4 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <select
              value={filters.status}
              onChange={(e) => handleFilterChange('status', e.target.value)}
              className={selectCls}
            >
              <option value="">All statuses</option>
              <option value="sent">Sent</option>
              <option value="delivered">Delivered</option>
              <option value="read">Read</option>
              <option value="deleted">Deleted</option>
            </select>
            <select
              value={filters.is_deleted}
              onChange={(e) => handleFilterChange('is_deleted', e.target.value)}
              className={selectCls}
            >
              <option value="">Deleted: any</option>
              <option value="false">Active</option>
              <option value="true">Soft-deleted</option>
            </select>
            <select
              value={filters.sender_kind}
              onChange={(e) => handleFilterChange('sender_kind', e.target.value)}
              className={selectCls}
            >
              <option value="">Sender: any</option>
              <option value="user">User</option>
              <option value="org">Org</option>
              <option value="platform">Platform</option>
            </select>
            <select
              value={filters.is_platform}
              onChange={(e) => handleFilterChange('is_platform', e.target.value)}
              className={selectCls}
            >
              <option value="">Source: any</option>
              <option value="false">User/org</option>
              <option value="true">Platform</option>
            </select>
            <select
              value={filters.attachment_kind}
              onChange={(e) => handleFilterChange('attachment_kind', e.target.value)}
              className={selectCls}
            >
              <option value="">Attachment: any</option>
              <option value="inquiry">Inquiry</option>
            </select>
            <select
              value={filters.sort}
              onChange={(e) => handleFilterChange('sort', e.target.value)}
              className={selectCls}
            >
              {MESSAGE_SORT_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  Sort: {o.label}
                </option>
              ))}
            </select>
            <select
              value={filters.order}
              onChange={(e) => handleFilterChange('order', e.target.value)}
              className={selectCls}
            >
              <option value="desc">Newest first</option>
              <option value="asc">Oldest first</option>
            </select>
            {hasFilters && (
              <button
                type="button"
                onClick={resetFilters}
                className="flex items-center gap-1.5 rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-600 hover:bg-gray-50"
              >
                <X className="h-4 w-4" />
                Clear
              </button>
            )}
          </div>
        </div>

        {isError && !forbidden && (
          <div className="rounded-lg border border-red-100 bg-red-50 px-4 py-3 text-sm text-red-700">
            {(error as Error)?.message || 'Failed to load messages'}
          </div>
        )}

        <div className="overflow-hidden rounded-xl border border-gray-100 bg-white shadow-sm">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-100">
              <thead>
                <tr className="bg-gray-50">
                  {['Message', 'Parties', 'Status', 'Entity', 'Updated'].map((h) => (
                    <th
                      key={h}
                      className="px-6 py-3.5 text-left text-xs font-semibold uppercase tracking-wider text-gray-500"
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {isLoading &&
                  Array.from({ length: 8 }).map((_, i) => (
                    <tr key={i}>
                      {Array.from({ length: 5 }).map((__, j) => (
                        <td key={j} className="px-6 py-4">
                          <Skeleton className="h-4 w-20" />
                        </td>
                      ))}
                    </tr>
                  ))}
                {!isLoading && messages.length === 0 && (
                  <tr>
                    <td colSpan={5} className="py-16 text-center text-sm text-gray-500">
                      No messages found
                    </td>
                  </tr>
                )}
                {!isLoading &&
                  messages.map((msg: AdminMessage) => (
                    <tr
                      key={msg.id || msg.hash}
                      className="cursor-pointer hover:bg-gray-50"
                      onClick={() => {
                        setSelectedRef(msg.id || msg.hash || null);
                        setIsDrawerOpen(true);
                      }}
                    >
                      <td className="max-w-xs px-6 py-4">
                        <div className="flex items-center gap-2">
                          <div className="truncate text-sm font-medium text-gray-900">
                            {msg.message || (msg.redacted ? '[redacted]' : '—')}
                          </div>
                          {isInquiryMessage(msg) && (
                            <span className="shrink-0 rounded-full bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold text-amber-800">
                              Inquiry
                            </span>
                          )}
                        </div>
                        <div className="mt-0.5 font-mono text-xs text-gray-400">
                          {(msg.id || msg.hash || '').slice(0, 12)}…
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600">
                        <MessageParties msg={msg} />
                      </td>
                      <td className="px-6 py-4">
                        <span
                          className={`rounded-full px-2.5 py-1 text-xs font-semibold uppercase ${
                            STATUS_BADGE[msg.status ?? 'sent'] ?? STATUS_BADGE.sent
                          }`}
                        >
                          {msg.status ?? 'sent'}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600">
                        {(() => {
                          const eid = messageEntity(msg);
                          return eid != null ? `#${eid}` : '—';
                        })()}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500">
                        {formatRelativeEpoch(msg.updated_at ?? msg.timestamp ?? msg.created_at)}
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
          <div className="border-t border-gray-100 px-6 py-3">
            <Pagination
              total={pagination?.total ?? 0}
              page={page}
              itemsPerPage={limit}
              limit={limit}
              setPage={handlePageChange}
              setItemsPerPage={handleLimitChange}
              showButtons
              showSelect
              selectOptions={[10, 25, 50, 100]}
            />
          </div>
        </div>

        {overview?.bans_active != null && overview.bans_active > 0 && (
          <p className="flex items-center gap-1.5 text-xs text-gray-500">
            <Ban className="h-3.5 w-3.5" />
            {overview.bans_active} active messaging ban{overview.bans_active === 1 ? '' : 's'}
          </p>
        )}
      </div>

      <MessageDetailsDrawer
        msgRef={selectedRef}
        isOpen={isDrawerOpen}
        setIsOpen={setIsDrawerOpen}
        canModerate={canModerate}
        canDelete={canDelete}
        onClose={() => {
          setIsDrawerOpen(false);
          setSelectedRef(null);
        }}
        onViewThread={(params) => {
          setThreadParams(params);
          setIsThreadOpen(true);
        }}
      />

      <PartyThreadDrawer
        params={threadParams}
        isOpen={isThreadOpen}
        setIsOpen={setIsThreadOpen}
        onClose={() => {
          setIsThreadOpen(false);
          setThreadParams(null);
        }}
        onSelectMessage={(ref) => {
          if (!ref) return;
          setSelectedRef(ref);
          setIsDrawerOpen(true);
        }}
      />
    </div>
  );
};

export default MessagingInboxPage;
